import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ReferenceRecordSchema,
  RoleSummarySchema,
  WorkflowAttentionItemSchema,
  WorkflowDefinitionSchema,
  WorkflowRecheckResultSchema,
  WorkflowSubjectTypeSchema,
  type ApproverRuleDto,
  type ReferenceRecord,
  type WorkflowDefinitionDto,
  type WorkflowSubjectType,
} from '@taqon/contracts';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { z } from 'zod';
import { errorMessage, getData, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { recordPath, viewCapability } from '../../shared/records/routes';
import { ConfirmDialog } from '../../shared/ui/confirm';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';

export const DEFINITIONS = '/api/v1/platform/workflow-definitions';
export const KEY = ['platform', 'workflow-definitions'];

const DIMENSION_LABELS: Readonly<Record<string, string>> = {
  department: 'Department',
  cost_center: 'Cost centre',
  project: 'Project',
  location: 'Location',
};
const DIMENSION_ATTRIBUTE: Readonly<Record<string, string>> = {
  department: 'departmentId',
  cost_center: 'costCenterId',
  project: 'projectId',
  location: 'locationId',
};
const OPERATORS = [
  ['eq', 'equals'],
  ['ne', 'does not equal'],
  ['gt', 'greater than'],
  ['gte', 'at least'],
  ['lt', 'less than'],
  ['lte', 'at most'],
  ['in', 'is one of'],
  ['not_in', 'is not one of'],
  ['is_null', 'is empty'],
  ['is_not_null', 'is not empty'],
] as const;

/**
 * Approval routes (SDD §14; FRD-CORE-011, CORE-013, CORE-014, CORE-017): for each record type that
 * goes through approval, the versions of its route. A new version is saved as a draft and put in
 * force deliberately; the version in force is never edited, so every decision stays traceable to
 * the route that governed it. The confirmed Release 1 routes (CLR-11) are configured here.
 */
export function WorkflowRoutesPage() {
  const [params, setParams] = useSearchParams();
  const selected = params.get('type');
  const types = useQuery({
    queryKey: ['platform', 'workflow-subject-types'],
    queryFn: () =>
      getData('/api/v1/platform/workflow-subject-types', z.array(WorkflowSubjectTypeSchema)),
  });
  const type = types.data?.find((t) => t.recordType === selected) ?? types.data?.[0];
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Approval routes"
        description="Who approves what, in which order. A record cannot be submitted until its route is in force."
      />
      <Attention />
      {types.isPending && <LoadingState label="Loading approval routes" />}
      {types.isError && <ErrorState message={types.error.message} />}
      {types.data?.length === 0 && (
        <Section>
          <EmptyState title="No record type goes through approval yet" />
        </Section>
      )}
      {type !== undefined && (
        <>
          <TextField
            select
            label="Record type"
            value={type.recordType}
            sx={{ maxWidth: 360 }}
            onChange={(e) => {
              setParams({ type: e.target.value });
            }}
          >
            {(types.data ?? []).map((t) => (
              <MenuItem key={t.recordType} value={t.recordType}>
                {t.displayName}
              </MenuItem>
            ))}
          </TextField>
          <Versions type={type} />
        </>
      )}
    </Stack>
  );
}

/**
 * Running approvals that wait on nobody (SDD §14.2, §14.5, §28.5): a step without an eligible
 * approver, or an escalation that found nobody. The administrators are notified of each; here they
 * find them again, fix the cause (a role, a user, a reporting line) and check again at once.
 */
function Attention() {
  const session = useSession();
  const queryClient = useQueryClient();
  const canChange = hasCapability(session.data, 'platform.workflow_definition:change');
  const items = useQuery({
    queryKey: ['platform', 'workflow-attention'],
    queryFn: () =>
      getData('/api/v1/platform/workflow-attention', z.array(WorkflowAttentionItemSchema)),
  });
  const recheck = useMutation({
    mutationFn: () =>
      postJson(
        '/api/v1/platform/workflow-attention/recheck',
        {},
        WorkflowRecheckResultSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['platform', 'workflow-attention'] });
    },
  });
  if (items.data?.length === 0 && !recheck.isSuccess) return null;
  return (
    <Section
      title="Approvals needing attention"
      actions={
        canChange ? (
          <Button
            size="small"
            variant="outlined"
            disabled={recheck.isPending}
            onClick={() => {
              recheck.mutate();
            }}
          >
            {recheck.isPending ? 'Checking…' : 'Check again now'}
          </Button>
        ) : undefined
      }
    >
      {items.isPending && <LoadingState label="Loading approvals needing attention" />}
      {items.isError && <ErrorState message={items.error.message} />}
      {recheck.isError && (
        <Alert severity="error" variant="outlined" sx={{ m: 2 }}>
          {errorMessage(recheck.error)}
        </Alert>
      )}
      {recheck.isSuccess && (
        <Alert severity="info" variant="outlined" sx={{ m: 2 }}>
          {recheck.data.reassigned === 1
            ? '1 step now has an approver.'
            : `${String(recheck.data.reassigned)} steps now have an approver.`}{' '}
          {recheck.data.stillWaiting === 0
            ? 'Nothing is waiting on nobody.'
            : `${String(recheck.data.stillWaiting)} still waiting.`}
        </Alert>
      )}
      {(items.data?.length ?? 0) > 0 && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 2 }}>
            These approvals are waiting on nobody. Fix the cause — assign the role to someone,
            reactivate a user, set the employee's reporting manager, or change the route for new
            submissions — then check again. Nothing here is ever approved automatically.
          </Typography>
          <Table size="small" aria-label="Approvals needing attention" sx={{ mt: 1 }}>
            <TableHead>
              <TableRow>
                <TableCell>Record</TableCell>
                <TableCell>Step</TableCell>
                <TableCell>Problem</TableCell>
                <TableCell>Route</TableCell>
                <TableCell>Since</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(items.data ?? []).map((item) => {
                const path = recordPath(item.subjectRecordType, item.subjectRecordId);
                const text =
                  item.subjectLabel ?? `${item.subjectRecordTypeName} ${item.subjectRecordId}`;
                return (
                  <TableRow key={item.stepId}>
                    <TableCell>
                      {path !== null &&
                      hasCapability(session.data, viewCapability(item.subjectRecordType)) ? (
                        <Link component={RouterLink} to={path}>
                          {text}
                        </Link>
                      ) : (
                        text
                      )}
                      <Typography variant="caption" color="text.secondary" component="div">
                        {item.subjectRecordTypeName}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.stepName}</TableCell>
                    <TableCell>
                      {item.reason === 'no_approver'
                        ? 'Nobody can decide this step'
                        : 'Its escalation found nobody'}
                    </TableCell>
                    <TableCell>
                      Version {item.routeVersion}: {item.routeName}
                    </TableCell>
                    <TableCell>{new Date(item.waitingSince).toLocaleString()}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </>
      )}
    </Section>
  );
}

function Versions({ type }: { type: WorkflowSubjectType }) {
  const session = useSession();
  // Role names for the steps table; a session without platform.role:view sees role codes.
  const rolesForNames = useQuery({
    queryKey: ['platform', 'roles'],
    queryFn: () => getData('/api/v1/platform/roles', z.array(RoleSummarySchema)),
    enabled: hasCapability(session.data, 'platform.role:view'),
    retry: false,
  });
  const roleNames = new Map((rolesForNames.data ?? []).map((r) => [r.code, r.name] as const));
  const canCreate = hasCapability(session.data, 'platform.workflow_definition:create');
  const canChange = hasCapability(session.data, 'platform.workflow_definition:change');
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState<WorkflowDefinitionDto | 'new' | null>(null);
  const [acting, setActing] = useState<{
    def: WorkflowDefinitionDto;
    action: 'activate' | 'deactivate';
  } | null>(null);
  const versions = useQuery({
    queryKey: [...KEY, type.recordType],
    queryFn: () =>
      getData(
        `${DEFINITIONS}?subjectRecordType=${type.recordType}`,
        z.array(WorkflowDefinitionSchema),
      ),
  });
  const recordLabels = useRecordLabels(
    type,
    (versions.data ?? []).flatMap((d) => d.steps.map((st) => st.condition)),
  );
  const act = useMutation({
    mutationFn: (input: { def: WorkflowDefinitionDto; action: 'activate' | 'deactivate' }) =>
      postJson(
        `${DEFINITIONS}/${input.def.id}/${input.action}`,
        {},
        WorkflowDefinitionSchema,
        session.data?.csrfToken ?? '',
        { expectedVersion: input.def.version },
      ),
    onSuccess: async () => {
      setActing(null);
      await queryClient.invalidateQueries({ queryKey: KEY });
    },
  });
  const active = versions.data?.find((d) => d.status === 'active');
  return (
    <Section
      title={`${type.displayName} routes`}
      actions={
        canCreate ? (
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setCreating(active ?? 'new');
            }}
          >
            New version
          </Button>
        ) : undefined
      }
    >
      {versions.isPending && <LoadingState label="Loading versions" />}
      {versions.isError && <ErrorState message={versions.error.message} />}
      {versions.data !== undefined && active === undefined && (
        <Alert severity="warning" variant="outlined" sx={{ m: 2 }}>
          No route is in force: {type.displayName.toLowerCase()} records cannot be submitted for
          approval until one is activated.
        </Alert>
      )}
      {versions.data?.length === 0 && <EmptyState title="No route has been configured" />}
      <Stack spacing={2} sx={{ p: 2 }}>
        {(versions.data ?? []).map((def) => (
          <Paper key={def.id} variant="outlined" sx={{ p: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                useFlexGap
              >
                <Typography variant="subtitle1" component="h3">
                  Version {def.definitionVersion}: {def.name}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  color={
                    def.status === 'active'
                      ? 'success'
                      : def.status === 'draft'
                        ? 'warning'
                        : 'default'
                  }
                  label={def.status === 'active' ? 'in force' : def.status}
                />
                <Typography variant="caption" color="text.secondary">
                  {def.status === 'active' ? 'in force' : def.status}
                  {def.validFrom !== null && ` since ${new Date(def.validFrom).toLocaleString()}`}
                  {def.validTo !== null && ` until ${new Date(def.validTo).toLocaleString()}`}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1}>
                {canCreate && (
                  <Button
                    size="small"
                    onClick={() => {
                      setCreating(def);
                    }}
                  >
                    Copy as new version
                  </Button>
                )}
                {canChange && def.status === 'draft' && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => {
                      act.reset();
                      setActing({ def, action: 'activate' });
                    }}
                  >
                    Put in force
                  </Button>
                )}
                {canChange && (def.status === 'active' || def.status === 'draft') && (
                  <Button
                    size="small"
                    color="warning"
                    onClick={() => {
                      act.reset();
                      setActing({ def, action: 'deactivate' });
                    }}
                  >
                    {def.status === 'active' ? 'Take out of force' : 'Retire draft'}
                  </Button>
                )}
              </Stack>
            </Stack>
            <Table
              size="small"
              aria-label={`Steps of version ${String(def.definitionVersion)}`}
              sx={{ mt: 1 }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Step</TableCell>
                  <TableCell>Approver</TableCell>
                  <TableCell>Applies when</TableCell>
                  <TableCell>Escalation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {def.steps.map((step) => (
                  <TableRow key={step.id}>
                    <TableCell>
                      {step.sequence}
                      {def.steps.filter((s) => s.sequence === step.sequence).length > 1 &&
                        ` (parallel, ${step.groupCompletion === 'all' ? 'all must approve' : 'any one'})`}
                    </TableCell>
                    <TableCell>{step.name}</TableCell>
                    <TableCell>{describeRule(step.approverRule, roleNames)}</TableCell>
                    <TableCell>{describeCondition(step.condition, type, recordLabels)}</TableCell>
                    <TableCell>
                      {step.escalationAfterHours === null || step.escalationTargetRule === null
                        ? '—'
                        : `After ${String(step.escalationAfterHours)} h to ${describeRule(step.escalationTargetRule, roleNames)}${step.escalationReassigns ? ' (may decide)' : ''}`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        ))}
      </Stack>
      {creating !== null && (
        <RouteDialog
          type={type}
          from={creating === 'new' ? null : creating}
          onClose={() => {
            setCreating(null);
          }}
        />
      )}
      <ConfirmDialog
        open={acting !== null}
        title={acting?.action === 'activate' ? 'Put route in force' : 'Take route out of force'}
        message={
          acting?.action === 'activate'
            ? `Version ${String(acting.def.definitionVersion)} governs every new submission from now on${active !== undefined ? `, replacing version ${String(active.definitionVersion)}` : ''}. Approvals already running keep their route.`
            : `Version ${String(acting?.def.definitionVersion ?? '')} will no longer be used. ${acting?.def.status === 'active' ? 'Until another version is put in force, new submissions are refused. ' : ''}Approvals already running continue.`
        }
        confirmLabel={acting?.action === 'activate' ? 'Put in force' : 'Confirm'}
        tone={acting?.action === 'activate' ? 'primary' : 'warning'}
        busy={act.isPending}
        error={act.isError ? errorMessage(act.error) : null}
        onCancel={() => {
          setActing(null);
        }}
        onConfirm={() => {
          if (acting !== null) act.mutate(acting);
        }}
      />
    </Section>
  );
}

export function describeRule(
  rule: ApproverRuleDto,
  roleNames: ReadonlyMap<string, string> = new Map(),
): string {
  const role = (code: string) => roleNames.get(code) ?? code;
  switch (rule.type) {
    case 'role':
      return `Holders of ${role(rule.roleCode)}`;
    case 'role_in_record_scope':
      return `Holders of ${role(rule.roleCode)} for the record's ${DIMENSION_LABELS[rule.dimension]?.toLowerCase() ?? rule.dimension}`;
    default:
      return 'Reporting manager';
  }
}

interface Term {
  attr: string;
  op: string;
  /** For `in` / `not in`, the values separated by commas. */
  value: string;
}

/** A group one level inside the step's condition (SDD §14.3: groups nest at most two deep). */
interface TermGroup {
  mode: 'all' | 'any';
  terms: Term[];
}

export type Member = Term | TermGroup;

export function isGroup(member: Member): member is TermGroup {
  return 'terms' in member;
}

const isList = (op: string) => op === 'in' || op === 'not_in';

function termOf(m: { attr: string; op: string; value?: unknown }): Term {
  return {
    attr: m.attr,
    op: m.op,
    value: Array.isArray(m.value)
      ? m.value.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ')
      : typeof m.value === 'string'
        ? m.value
        : m.value === undefined
          ? ''
          : JSON.stringify(m.value),
  };
}

function isTermLike(m: unknown): m is { attr: string; op: string; value?: unknown } {
  return typeof m === 'object' && m !== null && 'attr' in m;
}

function groupOf(m: unknown): { mode: 'all' | 'any'; members: unknown[] } | null {
  if (typeof m !== 'object' || m === null) return null;
  const g = m as { all?: unknown; any?: unknown };
  if (Array.isArray(g.all)) return { mode: 'all', members: g.all as unknown[] };
  if (Array.isArray(g.any)) return { mode: 'any', members: g.any as unknown[] };
  return null;
}

/** A stored condition as the editor holds it: a group of terms and groups of terms. */
export function membersOf(condition: unknown): { mode: 'all' | 'any'; members: Member[] } {
  if (isTermLike(condition)) return { mode: 'all', members: [termOf(condition)] };
  const top = groupOf(condition);
  if (top === null) return { mode: 'all', members: [] };
  const members: Member[] = [];
  for (const m of top.members) {
    if (isTermLike(m)) {
      members.push(termOf(m));
      continue;
    }
    const inner = groupOf(m);
    if (inner !== null) {
      members.push({ mode: inner.mode, terms: inner.members.filter(isTermLike).map(termOf) });
    }
  }
  return { mode: top.mode, members };
}

export function describeCondition(
  condition: unknown,
  type: WorkflowSubjectType,
  recordLabels: ReadonlyMap<string, string> = new Map(),
): string {
  const { mode, members } = membersOf(condition);
  if (members.length === 0) return 'Always';
  const label = (attr: string) =>
    type.routingAttributes.find((a) => a.name === attr)?.label ?? attr;
  const op = (code: string) => OPERATORS.find(([c]) => c === code)?.[1] ?? code;
  // A reference shows the record's code and name, not its id.
  const shown = (t: Term) =>
    isReference(type, t.attr)
      ? listValues(t.value)
          .map((id) => recordLabels.get(`${t.attr}:${id}`) ?? id)
          .join(', ')
      : t.value;
  const term = (t: Term) => `${label(t.attr)} ${op(t.op)}${t.value === '' ? '' : ` ${shown(t)}`}`;
  const join = (m: 'all' | 'any') => (m === 'all' ? ' and ' : ' or ');
  return members
    .map((m) => (isGroup(m) ? `(${m.terms.map(term).join(join(m.mode))})` : term(m)))
    .join(join(mode));
}

export function termComplete(t: Term): boolean {
  if (t.attr === '' || t.op === '') return false;
  if (t.op.startsWith('is_')) return true;
  if (isList(t.op)) return listValues(t.value).length > 0;
  return t.value.trim() !== '';
}

function listValues(value: string): string[] {
  return value
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '');
}

export interface StepDraft {
  sequence: string;
  name: string;
  groupCompletion: 'all' | 'any';
  rule: ApproverRuleDto;
  mode: 'all' | 'any';
  members: Member[];
  escalationHours: string;
  escalationRule: ApproverRuleDto | null;
  escalationReassigns: boolean;
}

function RouteDialog({
  type,
  from,
  onClose,
}: {
  type: WorkflowSubjectType;
  from: WorkflowDefinitionDto | null;
  onClose: () => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [name, setName] = useState(from?.name ?? `${type.displayName} approval`);
  const [steps, setSteps] = useState<StepDraft[]>(
    from === null
      ? [emptyStep(1)]
      : from.steps.map((s) => {
          const c = membersOf(s.condition);
          return {
            sequence: String(s.sequence),
            name: s.name,
            groupCompletion: s.groupCompletion,
            rule: s.approverRule,
            mode: c.mode,
            members: c.members,
            escalationHours: s.escalationAfterHours === null ? '' : String(s.escalationAfterHours),
            escalationRule: s.escalationTargetRule,
            escalationReassigns: s.escalationReassigns,
          };
        }),
  );
  const roles = useQuery({
    queryKey: ['platform', 'roles'],
    queryFn: () => getData('/api/v1/platform/roles', z.array(RoleSummarySchema)),
  });
  const save = useMutation({
    mutationFn: () =>
      postJson(
        DEFINITIONS,
        {
          subjectRecordType: type.recordType,
          name: name.trim(),
          steps: steps.map((s) => ({
            sequence: Number(s.sequence),
            name: s.name.trim(),
            groupCompletion: s.groupCompletion,
            approverRule: s.rule,
            ...(s.members.length > 0 ? { condition: conditionOf(s, type) } : {}),
            ...(s.escalationHours !== '' && s.escalationRule !== null
              ? {
                  escalationAfterHours: Number(s.escalationHours),
                  escalationTargetRule: s.escalationRule,
                  escalationReassigns: s.escalationReassigns,
                }
              : {}),
          })),
        },
        WorkflowDefinitionSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: KEY });
      onClose();
    },
  });
  const update = (index: number, patch: Partial<StepDraft>) => {
    setSteps(steps.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };
  const valid =
    name.trim() !== '' &&
    steps.length > 0 &&
    steps.every(
      (s) =>
        /^\d+$/.test(s.sequence) &&
        Number(s.sequence) >= 1 &&
        s.name.trim() !== '' &&
        ruleComplete(s.rule) &&
        s.members.every((m) =>
          isGroup(m) ? m.terms.length > 0 && m.terms.every(termComplete) : termComplete(m),
        ) &&
        (s.escalationHours === '' || (s.escalationRule !== null && ruleComplete(s.escalationRule))),
    );
  return (
    <Dialog open onClose={save.isPending ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>New version of the {type.displayName.toLowerCase()} route</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {save.isError && (
            <Alert severity="error" variant="outlined">
              {errorMessage(save.error)}
            </Alert>
          )}
          <TextField
            label="Route name"
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
          <Typography variant="body2" color="text.secondary">
            Steps run in order of their number. Steps with the same number run in parallel. A step
            with a condition applies only when the record meets it; otherwise it is skipped.
          </Typography>
          {steps.map((step, index) => (
            <Paper key={index} variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Order"
                    value={step.sequence}
                    sx={{ width: { sm: 90 } }}
                    onChange={(e) => {
                      update(index, { sequence: e.target.value.replace(/\D/g, '') });
                    }}
                  />
                  <TextField
                    label="Step name"
                    required
                    fullWidth
                    value={step.name}
                    onChange={(e) => {
                      update(index, { name: e.target.value });
                    }}
                  />
                  <TextField
                    select
                    label="Parallel steps complete when"
                    value={step.groupCompletion}
                    sx={{ minWidth: 200 }}
                    onChange={(e) => {
                      update(index, { groupCompletion: e.target.value === 'any' ? 'any' : 'all' });
                    }}
                  >
                    <MenuItem value="all">All approve</MenuItem>
                    <MenuItem value="any">Any one approves</MenuItem>
                  </TextField>
                  <IconButton
                    aria-label={`Remove step ${String(index + 1)}`}
                    disabled={steps.length === 1}
                    onClick={() => {
                      setSteps(steps.filter((_s, i) => i !== index));
                    }}
                  >
                    <DeleteOutlinedIcon />
                  </IconButton>
                </Stack>
                <RuleEditor
                  label="Approver"
                  rule={step.rule}
                  roles={roles.data ?? []}
                  type={type}
                  onChange={(rule) => {
                    update(index, { rule });
                  }}
                />
                <ConditionEditor
                  type={type}
                  mode={step.mode}
                  members={step.members}
                  onChange={(mode, members) => {
                    update(index, { mode, members });
                  }}
                />
                <Divider />
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ alignItems: { sm: 'center' } }}
                >
                  <TextField
                    label="Escalate after (hours)"
                    value={step.escalationHours}
                    sx={{ width: { sm: 200 } }}
                    onChange={(e) => {
                      const hours = e.target.value.replace(/\D/g, '');
                      update(index, {
                        escalationHours: hours,
                        escalationRule:
                          hours === ''
                            ? null
                            : (step.escalationRule ?? { type: 'reporting_manager' }),
                      });
                    }}
                    helperText="Leave empty for no escalation."
                  />
                  {step.escalationHours !== '' && step.escalationRule !== null && (
                    <>
                      <RuleEditor
                        label="Escalate to"
                        rule={step.escalationRule}
                        roles={roles.data ?? []}
                        type={type}
                        onChange={(rule) => {
                          update(index, { escalationRule: rule });
                        }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={step.escalationReassigns}
                            onChange={(e) => {
                              update(index, { escalationReassigns: e.target.checked });
                            }}
                          />
                        }
                        label="They may decide the step"
                      />
                    </>
                  )}
                </Stack>
              </Stack>
            </Paper>
          ))}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ alignSelf: 'flex-start' }}
            onClick={() => {
              const last = Math.max(...steps.map((s) => Number(s.sequence) || 0), 0);
              setSteps([...steps, emptyStep(last + 1)]);
            }}
          >
            Add step
          </Button>
          {steps.every((s) => s.members.length > 0) && (
            <Alert severity="warning" variant="outlined">
              Every step has a condition. A record that meets none of them cannot be submitted: it
              is never approved without an approver. Add a step that always applies if such records
              should be approved too.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={save.isPending} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!valid || save.isPending}
          onClick={() => {
            save.mutate();
          }}
        >
          {save.isPending ? 'Saving…' : 'Save as draft'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function emptyStep(sequence: number): StepDraft {
  return {
    sequence: String(sequence),
    name: '',
    groupCompletion: 'all',
    rule: { type: 'reporting_manager' },
    mode: 'all',
    members: [],
    escalationHours: '',
    escalationRule: null,
    escalationReassigns: false,
  };
}

export function ruleComplete(rule: ApproverRuleDto): boolean {
  return rule.type === 'reporting_manager' || rule.roleCode !== '';
}

export function RuleEditor({
  label,
  rule,
  roles,
  type,
  onChange,
}: {
  label: string;
  rule: ApproverRuleDto;
  roles: readonly { code: string; name: string; status: string }[];
  type: WorkflowSubjectType;
  onChange: (rule: ApproverRuleDto) => void;
}) {
  const dimensions = Object.entries(DIMENSION_ATTRIBUTE)
    .filter(([, attr]) => type.routingAttributes.some((a) => a.name === attr))
    .map(([dimension]) => dimension);
  const roleCode = rule.type === 'reporting_manager' ? '' : rule.roleCode;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <TextField
        select
        label={label}
        value={rule.type}
        sx={{ minWidth: 240 }}
        onChange={(e) => {
          const next = e.target.value;
          if (next === 'reporting_manager') onChange({ type: 'reporting_manager' });
          else if (next === 'role') onChange({ type: 'role', roleCode });
          else
            onChange({
              type: 'role_in_record_scope',
              roleCode,
              dimension: (dimensions[0] ?? 'department') as 'department',
            });
        }}
      >
        <MenuItem value="reporting_manager">The employee's reporting manager</MenuItem>
        <MenuItem value="role">Holders of a role</MenuItem>
        <MenuItem value="role_in_record_scope" disabled={dimensions.length === 0}>
          Holders of a role for the record's department, cost centre, project or location
        </MenuItem>
      </TextField>
      {rule.type !== 'reporting_manager' && (
        <TextField
          select
          label="Role"
          value={rule.roleCode}
          sx={{ minWidth: 200 }}
          onChange={(e) => {
            onChange({ ...rule, roleCode: e.target.value });
          }}
          helperText={
            rule.type === 'role'
              ? 'The role must be allowed to approve this record type. Only holders assigned without a department, cost centre, project or location scope are asked.'
              : 'The role must be allowed to approve this record type.'
          }
        >
          {roles.map((r) => (
            <MenuItem key={r.code} value={r.code} disabled={r.status !== 'active'}>
              {r.name}
              {r.status !== 'active' && ' (inactive: activate it to use it)'}
            </MenuItem>
          ))}
        </TextField>
      )}
      {rule.type === 'role_in_record_scope' && (
        <TextField
          select
          label="Matched on"
          value={rule.dimension}
          sx={{ minWidth: 160 }}
          onChange={(e) => {
            onChange({ ...rule, dimension: e.target.value as 'department' });
          }}
        >
          {dimensions.map((d) => (
            <MenuItem key={d} value={d}>
              {DIMENSION_LABELS[d]}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );
}

export function ConditionEditor({
  type,
  mode,
  members,
  onChange,
}: {
  type: WorkflowSubjectType;
  mode: 'all' | 'any';
  members: Member[];
  onChange: (mode: 'all' | 'any', members: Member[]) => void;
}) {
  if (type.routingAttributes.length === 0) return null;
  const blank = (): Term => ({
    attr: type.routingAttributes[0]?.name ?? '',
    op: 'eq',
    value: '',
  });
  const set = (i: number, member: Member) => {
    onChange(
      mode,
      members.map((m, j) => (j === i ? member : m)),
    );
  };
  const remove = (i: number) => {
    onChange(
      mode,
      members.filter((_m, j) => j !== i),
    );
  };
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="body2">Applies</Typography>
        {members.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            always
          </Typography>
        ) : (
          <ModeSelect
            label="Combine conditions"
            mode={mode}
            onChange={(next) => {
              onChange(next, members);
            }}
          />
        )}
      </Stack>
      {members.map((member, i) =>
        isGroup(member) ? (
          <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="body2">Group</Typography>
                <ModeSelect
                  label={`Combine group ${String(i + 1)}`}
                  mode={member.mode}
                  onChange={(next) => {
                    set(i, { ...member, mode: next });
                  }}
                />
                <IconButton
                  aria-label={`Remove group ${String(i + 1)}`}
                  onClick={() => {
                    remove(i);
                  }}
                >
                  <DeleteOutlinedIcon />
                </IconButton>
              </Stack>
              {member.terms.map((term, k) => (
                <TermRow
                  key={k}
                  type={type}
                  term={term}
                  removeLabel={`Remove condition ${String(k + 1)} of group ${String(i + 1)}`}
                  onChange={(next) => {
                    set(i, { ...member, terms: member.terms.map((t, j) => (j === k ? next : t)) });
                  }}
                  onRemove={() => {
                    set(i, { ...member, terms: member.terms.filter((_t, j) => j !== k) });
                  }}
                />
              ))}
              <Button
                size="small"
                sx={{ alignSelf: 'flex-start' }}
                onClick={() => {
                  set(i, { ...member, terms: [...member.terms, blank()] });
                }}
              >
                Add condition to group
              </Button>
            </Stack>
          </Paper>
        ) : (
          <TermRow
            key={i}
            type={type}
            term={member}
            removeLabel={`Remove condition ${String(i + 1)}`}
            onChange={(next) => {
              set(i, next);
            }}
            onRemove={() => {
              remove(i);
            }}
          />
        ),
      )}
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          onClick={() => {
            onChange(mode, [...members, blank()]);
          }}
        >
          Add condition
        </Button>
        <Button
          size="small"
          onClick={() => {
            onChange(mode, [...members, { mode: 'any', terms: [blank()] }]);
          }}
        >
          Add group
        </Button>
      </Stack>
    </Stack>
  );
}

function ModeSelect({
  label,
  mode,
  onChange,
}: {
  label: string;
  mode: 'all' | 'any';
  onChange: (mode: 'all' | 'any') => void;
}) {
  return (
    <TextField
      select
      size="small"
      value={mode}
      slotProps={{ htmlInput: { 'aria-label': label } }}
      onChange={(e) => {
        onChange(e.target.value === 'any' ? 'any' : 'all');
      }}
    >
      <MenuItem value="all">when all of these hold</MenuItem>
      <MenuItem value="any">when any of these holds</MenuItem>
    </TextField>
  );
}

function TermRow({
  type,
  term,
  removeLabel,
  onChange,
  onRemove,
}: {
  type: WorkflowSubjectType;
  term: Term;
  removeLabel: string;
  onChange: (term: Term) => void;
  onRemove: () => void;
}) {
  const kind = type.routingAttributes.find((a) => a.name === term.attr)?.kind ?? 'text';
  const operators = OPERATORS.filter(([code]) => !ORDERED.has(code) || ORDERABLE.has(kind));
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <TextField
        select
        size="small"
        label="Attribute"
        value={term.attr}
        sx={{ minWidth: 180 }}
        onChange={(e) => {
          const nextKind =
            type.routingAttributes.find((a) => a.name === e.target.value)?.kind ?? 'text';
          // A test or value that does not suit the new attribute's kind starts again.
          const keepOp = !ORDERED.has(term.op) || ORDERABLE.has(nextKind);
          onChange({
            attr: e.target.value,
            op: keepOp ? term.op : 'eq',
            value: nextKind === kind ? term.value : '',
          });
        }}
      >
        {type.routingAttributes.map((a) => (
          <MenuItem key={a.name} value={a.name}>
            {a.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        label="Test"
        value={term.op}
        sx={{ minWidth: 160 }}
        onChange={(e) => {
          onChange({ ...term, op: e.target.value });
        }}
      >
        {operators.map(([code, label]) => (
          <MenuItem key={code} value={code}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      {!term.op.startsWith('is_') &&
        (isReference(type, term.attr) ? (
          <ReferencePicker
            type={type}
            attr={term.attr}
            multiple={isList(term.op)}
            value={term.value}
            onChange={(value) => {
              onChange({ ...term, value });
            }}
          />
        ) : kind === 'boolean' && !isList(term.op) ? (
          <TextField
            select
            size="small"
            label="Value"
            value={term.value}
            sx={{ minWidth: 120 }}
            onChange={(e) => {
              onChange({ ...term, value: e.target.value });
            }}
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </TextField>
        ) : (
          <TextField
            size="small"
            label={isList(term.op) ? 'Values' : 'Value'}
            value={term.value}
            helperText={valueHelp(kind, isList(term.op))}
            onChange={(e) => {
              onChange({ ...term, value: e.target.value });
            }}
          />
        ))}
      <IconButton aria-label={removeLabel} onClick={onRemove}>
        <DeleteOutlinedIcon />
      </IconButton>
    </Stack>
  );
}

/** A reference attribute whose records can be chosen by code or name. */
function isReference(type: WorkflowSubjectType, attr: string): boolean {
  const a = type.routingAttributes.find((r) => r.name === attr);
  return a?.kind === 'reference' && a.searchable === true;
}

const RECORD_ID = /^[1-9]\d{0,18}$/;

const recordsUrl = (type: WorkflowSubjectType, attr: string) =>
  `/api/v1/platform/workflow-subject-types/${type.recordType}/attributes/${attr}/records`;

/** Code-and-name labels of the records the stored conditions name, keyed `attr:id`. */
function useRecordLabels(
  type: WorkflowSubjectType,
  conditions: readonly unknown[],
): ReadonlyMap<string, string> {
  const wanted = new Map<string, Set<string>>();
  for (const condition of conditions) {
    for (const m of membersOf(condition).members) {
      for (const t of isGroup(m) ? m.terms : [m]) {
        if (!isReference(type, t.attr)) continue;
        const ids = wanted.get(t.attr) ?? new Set<string>();
        // A route saved before ids were required may hold a code; it is shown as stored.
        for (const id of listValues(t.value)) if (RECORD_ID.test(id)) ids.add(id);
        wanted.set(t.attr, ids);
      }
    }
  }
  const keys = [...wanted.entries()].map(([attr, ids]) => [attr, [...ids].sort().join(',')]);
  const labels = useQuery({
    queryKey: ['platform', 'workflow-record-labels', type.recordType, keys],
    enabled: keys.length > 0,
    queryFn: async () => {
      const out = new Map<string, string>();
      for (const [attr, ids] of keys) {
        if (attr === undefined || ids === undefined || ids === '') continue;
        const rows = await getData(
          `${recordsUrl(type, attr)}?ids=${ids}`,
          z.array(ReferenceRecordSchema),
        );
        for (const r of rows) out.set(`${attr}:${r.id}`, r.label);
      }
      return out;
    },
  });
  return labels.data ?? new Map<string, string>();
}

/**
 * Chooses the records a reference condition names by code or name (SDD §28.5); the condition
 * stores their ids, so the route never depends on a code that could be retyped.
 */
function ReferencePicker({
  type,
  attr,
  multiple,
  value,
  onChange,
}: {
  type: WorkflowSubjectType;
  attr: string;
  multiple: boolean;
  value: string;
  onChange: (value: string) => void;
}) {
  const [input, setInput] = useState('');
  const ids = listValues(value).filter((id) => RECORD_ID.test(id));
  const chosen = useQuery({
    queryKey: ['platform', 'workflow-record-labels', type.recordType, attr, ids.join(',')],
    enabled: ids.length > 0,
    queryFn: () =>
      getData(`${recordsUrl(type, attr)}?ids=${ids.join(',')}`, z.array(ReferenceRecordSchema)),
  });
  const options = useQuery({
    queryKey: ['platform', 'workflow-record-search', type.recordType, attr, input.trim()],
    enabled: input.trim() !== '',
    queryFn: () =>
      getData(
        `${recordsUrl(type, attr)}?q=${encodeURIComponent(input.trim())}`,
        z.array(ReferenceRecordSchema),
      ),
  });
  const selected: ReferenceRecord[] = ids.map(
    (id) => chosen.data?.find((r) => r.id === id) ?? { id, label: id },
  );
  const common = {
    size: 'small' as const,
    sx: { minWidth: 260 },
    options: options.data ?? [],
    getOptionLabel: (o: ReferenceRecord) => o.label,
    isOptionEqualToValue: (a: ReferenceRecord, b: ReferenceRecord) => a.id === b.id,
    filterOptions: (o: ReferenceRecord[]) => o,
    onInputChange: (_e: unknown, text: string, reason: string) => {
      if (reason === 'input') setInput(text);
    },
    noOptionsText: input.trim() === '' ? 'Type part of a code or name' : 'No record matches',
  };
  const field = (params: object) => (
    <TextField
      {...params}
      label={multiple ? 'Values' : 'Value'}
      helperText="Type part of a code or name."
    />
  );
  return multiple ? (
    <Autocomplete
      {...common}
      multiple
      value={selected}
      onChange={(_e, next) => {
        onChange(next.map((r) => r.id).join(', '));
      }}
      renderInput={field}
    />
  ) : (
    <Autocomplete
      {...common}
      value={selected[0] ?? null}
      onChange={(_e, next) => {
        onChange(next?.id ?? '');
      }}
      renderInput={field}
    />
  );
}

/** Ordering tests (`greater than` …) apply to amounts and dates only; other kinds compare equal. */
const ORDERED: ReadonlySet<string> = new Set(['gt', 'gte', 'lt', 'lte']);
const ORDERABLE: ReadonlySet<string> = new Set(['amount', 'date']);

/** How a value must be written for the attribute's kind (the API refuses anything else). */
function valueHelp(kind: string, list: boolean): string | undefined {
  const each = list ? 'Separate the values with commas. ' : '';
  switch (kind) {
    case 'amount':
      return `${each}Digits only, e.g. 10000.00 — no commas or currency.`;
    case 'date':
      return `${each}As YYYY-MM-DD.`;
    case 'reference':
      return `${each}The record's id.`;
    default:
      return list ? 'Separate the values with commas.' : undefined;
  }
}

/**
 * The structured condition the API evaluates (SDD §14.3): amounts stay strings for Decimal, and
 * the list tests carry an array of values.
 */
export function conditionOf(step: StepDraft, type: WorkflowSubjectType): unknown {
  const typed = (attr: string, value: string): string | boolean =>
    type.routingAttributes.find((a) => a.name === attr)?.kind === 'boolean'
      ? value === 'true'
      : value;
  const term = (t: Term) => {
    if (t.op.startsWith('is_')) return { attr: t.attr, op: t.op };
    if (isList(t.op)) {
      return { attr: t.attr, op: t.op, value: listValues(t.value).map((v) => typed(t.attr, v)) };
    }
    return { attr: t.attr, op: t.op, value: typed(t.attr, t.value.trim()) };
  };
  const group = (mode: 'all' | 'any', items: unknown[]) =>
    mode === 'all' ? { all: items } : { any: items };
  return group(
    step.mode,
    step.members.map((m) => (isGroup(m) ? group(m.mode, m.terms.map(term)) : term(m))),
  );
}
